import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { joinUrl, endpoints } from '../api/config';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { BUTTON_STYLE } from '../constants/styles';
import jyBlackLogo from '../assets/JY BLACK.png';
import LoadingScreen from '../components/LoadingScreen';

const TwoFactorLogin = () => {
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const otpInputRef = useRef(null);

  useEffect(() => {
    // Check if username and password exist in session storage
    const username = sessionStorage.getItem('username');
    const tempPassword = sessionStorage.getItem('tempPassword');
    
    if (!username || !tempPassword) {
      // No username or password found, redirect back to login
      navigate('/login', { replace: true });
      return;
    }

    // Check if user is already fully authenticated
    const token = sessionStorage.getItem('token');
    const twoFactorVerified = sessionStorage.getItem('twoFactorVerified') === 'true';
    if (token && twoFactorVerified) {
      // User is already fully authenticated, redirect to dashboard
      navigate('/dashboard', { replace: true });
      return;
    }

    // Automatically focus the TOTP code input field as soon as page mounts
    const focusTimer = setTimeout(() => {
      otpInputRef.current?.focus();
    }, 50);

    return () => clearTimeout(focusTimer);
  }, [navigate]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) return;
    
    setError('');

    if (!totpCode.trim()) {
      setError('Please enter the TOTP code');
      return;
    }

    // Show loading screen immediately when user clicks verify
    setShowLoadingScreen(true);

    try {
      const username = sessionStorage.getItem('username');
      const tempPassword = sessionStorage.getItem('tempPassword');
      
      if (!username || !tempPassword) {
        setError('Authentication data not found. Please log in again.');
        setShowLoadingScreen(false);
        return;
      }

      const requestBody = {
        username: username,
        password: tempPassword,
        totp_code: totpCode
      };

      console.log('2FA Login Request:', { username, password: '***', totp_code: '***' });

      const response = await axios.post(joinUrl(import.meta.env.VITE_API_URL, endpoints.loginWith2fa), requestBody);

      console.log('2FA Login Response:', response.data);

      if (response.data.access_token) {
        // Store features in session storage
        if (response.data.features) {
          sessionStorage.setItem('adminFeatures', JSON.stringify(response.data.features));
        }
        
        // Store the new token and proceed to main app
        login(response.data.access_token, { username }, true);
        sessionStorage.setItem('intermediateFeatureCompleted', 'true');
        // Clear temporary password
        sessionStorage.removeItem('tempPassword');
        
        // Navigate to dashboard after 2 seconds
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      } else {
        setError('Invalid response from server');
        setTotpCode('');
        setShowLoadingScreen(false);
      }
    } catch (err) {
      console.error('2FA login error:', err);
      setTotpCode('');
      setShowLoadingScreen(false);
      
      if (err.response?.status === 401) {
        setError('Invalid TOTP code. Please try again.');
      } else if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          setError(`Validation error: ${errorDetail[0].msg || errorDetail[0]}`);
        } else {
          setError('Invalid request format. Please check your input.');
        }
      } else if (!err.response) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError(err.response?.data?.detail || 'An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleBackToLogin = () => {
    // Clear all stored authentication data and redirect to login
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('tempPassword');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('tokenCreationTime');
    sessionStorage.removeItem('twoFactorVerified');
    sessionStorage.removeItem('intermediateFeatureCompleted');
    
    // Navigate to login with manual flag
    navigate('/login?manual=true', { replace: true });
  };

  // Show loading screen if 2FA was successful
  if (showLoadingScreen) {
    return <LoadingScreen />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e3f0ff 0%, #fafcff 100%)',
        position: 'relative',
      }}
    >
      <Button
        variant="outlined"
        onClick={handleBackToLogin}
        sx={{ 
          position: 'absolute',
          top: 20,
          left: 20,
          py: 1,
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: 1,
          ...BUTTON_STYLE 
        }}
      >
        Back to Login
      </Button>
      
      <Container maxWidth="xs">
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'white',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          }}
        >
          <img src={jyBlackLogo} alt="JY Logo" style={{ width: 90, paddingBottom: 20 }} />
          <Typography component="h1" variant="h5" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Please enter the 6-digit code from your authenticator app.
          </Typography>
          
          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            noValidate 
            sx={{ width: '100%', mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              name="totp"
              label="TOTP Code"
              type="text"
              id="totp"
              placeholder="Enter 6-digit code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              disabled={loading}
              autoFocus
              inputRef={otpInputRef}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              sx={{ mb: 2 }}
            />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Button
              onClick={handleSubmit}
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ 
                mt: 3,
                mb: 2,
                py: 1.5, 
                fontWeight: 600,
                fontSize: 16,
                letterSpacing: 1,
                ...BUTTON_STYLE 
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Verifying...
                </>
              ) : (
                'Verify and Login'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TwoFactorLogin;
